<template>
  <AlarmModal :alarms="alarms" :selected-id="selectedAlarmId"></AlarmModal>
  <div class="overlay">
    <MainMap
      :center="center"
      :sensor-readings="sensorReadings"
      :vessels="filteredVessels"
      :cableLength="cableLength"
    />
  </div>

  <main>
    <DateTimeFilterBar :is-loading="isLoading">
      <template v-slot:search>
        <MsSearch
          v-show="filterStore.isVesselActive"
          :place-holder="'Search for vessel'"
          @update-text="search"
        ></MsSearch
      ></template>
    </DateTimeFilterBar>
    <div class="d-flex flex-column mt-4">
      <div class="d-flex flex-row mt-3 toggle-click-parent">
        <div class="toggle-dropdown toggle-click-child">
          <DropdownToggleTemplate
            title="DTS"
            :disabled="projectStore.selectedProject?.dtsHomePageDisabled"
          />
        </div>
        <div class="toggle-click-child">
          <DropdownToggleTemplate
            title="DAS"
            :disabled="projectStore.selectedProject?.dasHomePageDisabled"
          />
        </div>
      </div>
      <div class="d-flex flex-row mt-3 toggle-click-parent">
        <div class="toggle-dropdown toggle-click-child">
          <DropdownToggleTemplate
            title="Vessel traffic"
            :disabled="projectStore.selectedProject?.vesselsHomePageDisabled"
          />
        </div>
        <div class="toggle-dropdown toggle-click-child">
          <DropdownToggleTemplate
            title="DoB"
            :disabled="projectStore.selectedProject?.doBHomePageDisabled"
          />
        </div>
      </div>
      <div class="d-flex flex-row mt-3 toggle-click-parent">
        <div class="toggle-click-child">
          <DropDownAlarms
            @alarm-click="openModal"
            :alarms="alarms"
            :disabled="projectStore.selectedProject?.alarmLogsDisabled"
            :alarm-count="alarmCount"
          />
        </div>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import AlarmModal from '@/components/modal/AlarmModal.vue'
import DateTimeFilterBar from '../components/filters/datetime/DateTimeFilterBar.vue'
import DropdownToggleTemplate from '../components/filters/dropdown/DropdownToggleTemplate.vue'
import DropDownAlarms from '../components/filters/dropdown/DropDownAlarms.vue'
import MainMap from '@/components/maps/MainMap.vue'
import { Alarm, MapCableDataPoint, CableVessel, HomeClient } from '@/server/MakeSenseApp-backend'
import { computed, onMounted, ref, watch } from 'vue'
import { Modal } from 'bootstrap'
import { useProjectStore } from '@/stores/ProjectStore'
import { useDateTimeStore } from '@/stores/DateTimeFilterStore'
import { watchDebounced } from '@vueuse/core'
import { usePopupStore } from '@/stores/PopupStore'
import { useFilterStore } from '@/stores/HomeFilterStore'

import { requiredInject } from '@/utilities/requiredInject'
import MsSearch from '@/components/general/input/MsSearch.vue'

const client = requiredInject<HomeClient>('HomeClient')

const sensorReadings = ref<MapCableDataPoint[]>([])
const center = ref<number[]>([0, 0])
const alarms = ref<Alarm[]>([])
const alarmCount = ref<number>(0)
const cableLength = ref<number>(0)
const vessels = ref<CableVessel[]>([])

const isLoading = ref<boolean>(false)

let projectStore = useProjectStore()
let dateTimeStore = useDateTimeStore()
let popupStore = usePopupStore()
let filterStore = useFilterStore()

var selectedAlarmId = ref<string>('0')

const searchValue = ref('')

const filteredVessels = computed(() => {
  const search = searchValue.value.toLowerCase().trim()
  return vessels.value.filter(
    (vessel) =>
      vessel.vesselName?.toLowerCase().includes(search) ||
      vessel.mmsi?.toString()?.toLowerCase().includes(search)
  )
})

const latestRequestId = ref(0)

function openModal(selectedId?: string) {
  var element = document.getElementById('alarmModal')

  if (element && selectedId !== undefined) {
    selectedAlarmId.value = selectedId
    var modal = new Modal(element)
    modal.show()
  }
}

async function reloadProjectData(projectId: string, dateTime: Date | null, recenter: boolean) {
  popupStore.closePopup()

  if (projectStore.selectedProject?.dtsHomePageDisabled && filterStore.isDTSActive) {
    filterStore.setDTS()
  }
  if (projectStore.selectedProject?.dasHomePageDisabled && filterStore.isDASActive) {
    filterStore.setDAS()
  }
  if (projectStore.selectedProject?.doBHomePageDisabled && filterStore.isDoBActive) {
    filterStore.setDoB()
  }
  if (projectStore.selectedProject?.vesselsHomePageDisabled && filterStore.isVesselActive) {
    filterStore.setVessel()
  }

  const requestId = ++latestRequestId.value
  isLoading.value = true

  try {
    console.log('Fetching using datetime ' + dateTime)
    const data = await client.getCableOverview(projectId, dateTime)

    // Apply the result only if it's the latest request
    if (requestId === latestRequestId.value) {
      alarms.value = data?.alarms ?? []
      vessels.value = data?.vessels ?? []
      alarmCount.value = data?.alarmCount ?? 0

      sensorReadings.value = data?.cable ?? []
      cableLength.value = data?.cableLength ?? 0

      if (recenter) {
        center.value = [data?.midPoint?.easting ?? 0, data.midPoint?.northing ?? 0]
      }
    }

    dateTimeStore.update()
  } finally {
    // Set loading to false only if this request was the latest
    if (requestId === latestRequestId.value) {
      isLoading.value = false
    }
  }
}

function search(value: string) {
  searchValue.value = value
}

onMounted(() => {
  watch(
    () => projectStore.projectId,
    () => {
      reloadProjectData(projectStore.projectId, dateTimeStore.dateTime, true)
    }
  )

  watchDebounced(
    () => dateTimeStore.dateTime,
    () => {
      reloadProjectData(projectStore.projectId, dateTimeStore.dateTime, false)
    },
    { debounce: 300 }
  )

  reloadProjectData(projectStore.projectId, dateTimeStore.dateTime, true)
})
</script>

<style scoped>
.FilterBar,
.d-flex {
  z-index: 2;
}

main {
  padding: 16px;
}

.overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
}

.toggle-dropdown {
  margin-right: 16px;
}

.toggle-click-parent {
  pointer-events: none;
}

.toggle-click-child {
  pointer-events: auto;
}
</style>
